import type { ComponentProps } from "react";
import { Col, Form, Row, Tooltip } from "antd";
import { stripWhitespace } from "../../../../../utils/formNormalizers";
import {
  CardHeading,
  CardSubtitle,
  CardTitle,
  DisabledFieldWrap,
  FormItem,
  PasswordField,
  SectionCard,
} from "../../style";

const { useWatch } = Form;

const PASSWORD_REGEX =
  /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,20}$/;
const PASSWORD_RULES_MESSAGE =
  "8-20 chars, with uppercase, lowercase, number, and special char (@$!%*?&).";
const GOOGLE_DISABLED_MESSAGE =
  "Not available for accounts that signed in with Google";

type PasswordInputProps = ComponentProps<typeof PasswordField> & {
  isGoogleUser: boolean;
};

function PasswordInput({ isGoogleUser, ...rest }: PasswordInputProps) {
  if (!isGoogleUser) {
    return <PasswordField {...rest} />;
  }
  return (
    <Tooltip title={GOOGLE_DISABLED_MESSAGE}>
      <DisabledFieldWrap>
        <PasswordField {...rest} disabled />
      </DisabledFieldWrap>
    </Tooltip>
  );
}

interface UpdatePasswordCardProps {
  isGoogleUser: boolean;
}

export default function UpdatePasswordCard({
  isGoogleUser,
}: UpdatePasswordCardProps) {
  const currentPwd = useWatch("currentPassword");
  const newPwd = useWatch("newPassword");
  const confirmPwd = useWatch("confirmPassword");
  const anyFilled = !isGoogleUser && !!(currentPwd || newPwd || confirmPwd);

  return (
    <SectionCard>
      <CardHeading vertical gap={2}>
        <CardTitle level={3}>Update Password</CardTitle>
        <CardSubtitle>
          {isGoogleUser
            ? "Your account uses Google sign-in, so the password cannot be changed here."
            : "Keep your account secure with a new password"}
        </CardSubtitle>
      </CardHeading>

      <Row gutter={[24, 20]}>
        <Col xs={24} md={8}>
          <FormItem
            name="currentPassword"
            label="Current Password"
            normalize={stripWhitespace}
            rules={[
              {
                required: anyFilled,
                message: "Please enter your current password",
              },
            ]}
          >
            <PasswordInput
              placeholder="Enter current password"
              isGoogleUser={isGoogleUser}
            />
          </FormItem>
        </Col>

        <Col xs={24} md={8}>
          <FormItem
            name="newPassword"
            label="New Password"
            normalize={stripWhitespace}
            rules={[
              {
                required: anyFilled,
                message: "Please enter a new password",
              },
              {
                pattern: PASSWORD_REGEX,
                message: PASSWORD_RULES_MESSAGE,
              },
            ]}
          >
            <PasswordInput
              placeholder="Enter new password"
              isGoogleUser={isGoogleUser}
            />
          </FormItem>
        </Col>

        <Col xs={24} md={8}>
          <FormItem
            name="confirmPassword"
            label="Confirm New Password"
            normalize={stripWhitespace}
            dependencies={["newPassword"]}
            rules={[
              {
                required: anyFilled,
                message: "Please confirm your new password",
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("newPassword") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error("Passwords do not match"));
                },
              }),
            ]}
          >
            <PasswordInput
              placeholder="Confirm new password"
              isGoogleUser={isGoogleUser}
            />
          </FormItem>
        </Col>
      </Row>
    </SectionCard>
  );
}
